import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceDenormalized,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import { ChevronLeft } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { NotFound } from '../../../../../components/NotFound';
import { useTranslation } from '../../../../../i18next';
import { Page } from '../../../../../layout/Page';
import { PageBody } from '../../../../../layout/PageBody';
import { LightMdToJsx } from '../../../../../services/formatter';
import { useStandaloneAppContext } from '../../../../hooks/useStandaloneAppContext';
import { ParticipantInfo } from '../../components/ParticipantInfo';
import { Task } from '../../components/Task';
import { getSubTaskContext } from '../../service';
import { useProductAnalytics } from '../../services/productAnalytics';

const BackContainer = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  color: p.theme.custom.colors.primary.main,
  marginBottom: p.theme.spacing(4),
  gap: p.theme.spacing()
}));

const Back = ({ d }: { d: IExperienceDenormalized }) => {
  const { t } = useTranslation();
  const { routing } = useStandaloneAppContext();
  const body = (
    <BackContainer>
      <ChevronLeft size={16} />
      <LightMdToJsx component="span">
        {t('standaloneApp.backToOverview', { title: d.title })}
      </LightMdToJsx>
    </BackContainer>
  );
  return routing.toOverview.href ? (
    <Link to={routing.toOverview.href(d.accessToken)}>{body}</Link>
  ) : (
    <div
      role="button"
      onClick={() => routing.toOverview.onClick(d.accessToken)}
      style={{ cursor: 'pointer' }}
    >
      {body}
    </div>
  );
};

export const StandaloneAppPageTaskDetailsInner = ({
  experience,
  taskId,
  subTaskId,
  action
}: {
  experience: Doc<IExperienceDenormalized>;
  taskId: string;
  subTaskId?: string;
  action?: 'DONE';
}) => {
  const d = experience.data;
  const task = d.tasks.find((x) => x.id === taskId);
  const { trackEvent } = useProductAnalytics(d.accessToken);

  useEffect(() => {
    trackEvent({
      eventType: PAEventType.EMPLOYEE_EXPERIENCE_TASK,
      name: 'Employee Task Page Loaded',
      Explanation:
        'A participant or supporting actor loaded the page for a particular task in the employee app',
      'Task ID': taskId
    });
  }, [trackEvent, taskId]);

  return (
    <PageBody>
      <Page width="M">
        <Back d={d} />
        {d.actor !== PARTICIPANT_ACTOR_KEY && <ParticipantInfo d={d} />}
        {task && (
          <Task
            taskId={task.id}
            context={{
              title: task.title,
              status: task.status,
              startDate: task.start,
              endDate: task.end,
              completedAt: task.completedAt,
              completionCriteria: task.customCompletionCriteria,
              subtasks: task.blocks,

              org: experience.data.org,
              user: experience.data.user,
              userId: experience.data.userId,

              subTaskContext: getSubTaskContext(
                task.id,
                experience,
                trackEvent
              ),
              additionalInfo: task.additionalInfo || null,
              customHints: experience.data.customHints
            }}
            variant="expanded"
            highlightedSubTaskId={subTaskId}
          />
        )}
        {!task && <NotFound />}
      </Page>
    </PageBody>
  );
};
